import { useState, memo, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { userDashboardRoute } from "../../../../utils/routes/Routes";
import RowContainer from "../../../common/RowContainer";
import { TableWrapper, StyledTable, TableHeader } from "../../style";
import "./ProjectStyle.less";
import { FilterAlt, FilterAltOff } from "@mui/icons-material";
import { TableCell, Typography } from "@mui/material";
import { changePageNumberTickets } from "../../../../redux/features/tickets/ticketsSlice";
import MultipleFilterComponent from "../../../common/MultipleFilterComponent";
import { RenderColoredCircleIconWithStatus } from "../../../common/RenderColoredCircleIconWithStatus";
import { FormatDateTime } from "../../../../utils/helpers/FormatDateTime";
import { FormattedMessage } from "react-intl";
import { DashboardButton } from "../../../common/DashboardButton";
import { FilterButton } from "../../../common/FilterButton";

export const ProjectsList = memo(function ({
  projects,
  paginationData,
  notFilteredProjects,
  onFilterStateChange,
  onProjectsPageChange,
}) {
  // INIT
  const dispatch = useDispatch();

  // STATE
  const [filters, setFilters] = useState({
    project_status: null,
    vehicle_type: null,
    brand: null,
    model: null,
    created_at: null,
    modified_at: null,
  });
  const [manualProjectsFilters, setManualProjectsFilters] = useState({
    brand: null,
    model: null,
  });

  const [isFilteringActive, setIsFilteringActive] = useState(false);
  const [isFilteringManualProjectsActive, setIsFilteringManualProjectsActive] =
    useState(false);

  // FUNCTIONS
  const resetFiltersAndPage = useCallback(() => {
    setFilters({
      project_status: null,
      vehicle_type: null,
      brand: null,
      model: null,
      created_at: null,
      modified_at: null,
    });
    setManualProjectsFilters({
      brand: null,
      model: null,
    });
    onFilterStateChange({
      project_status: null,
      vehicle_type: null,
      brand: null,
      model: null,
      created_at: null,
      modified_at: null,
    });
  }, [dispatch, onFilterStateChange]);

  const handleFilterChange = (key, value) => {
    // lift the state up to the parent (UserPage)
    const newFilterValue = value;
    setFilters((prevState) => {
      return { ...prevState, [key]: newFilterValue };
    });
    onFilterStateChange((filters) => ({
      ...filters,
      [key]: newFilterValue,
    }));
    dispatch(changePageNumberTickets(1));
  };
  const handleFilterChangeManual = (key, value) => {
    setManualProjectsFilters((prevState) => {
      return { ...prevState, [key]: value };
    });
    onFilterStateChange((filters) => ({
      ...filters,
      text_field: value,
    }));
  };

  const handleToggleFilters = () => {
    setIsFilteringActive((prevState) => {
      const newState = !prevState;
      if (newState) {
        resetFiltersAndPage();
      }
      return newState;
    });
    setIsFilteringManualProjectsActive(false);
  };

  const handleToggleManualFilters = () => {
    setIsFilteringManualProjectsActive((prevState) => {
      const newState = !prevState;
      if (newState) {
        resetFiltersAndPage();
        setIsFilteringActive(false);
      }
      return newState;
    });
    setIsFilteringActive(false);
  };

  const renderProjectsRows = () => {
    return projects?.paginated_results?.map((row, index) => {
      const projTextfieldArray =
        row?.project_textfield && row.project_textfield.split(", ");
      return (
        <tr key={index}>
          {/* <TableCell>{row.id}</TableCell> */}
          <TableCell>
            <RenderColoredCircleIconWithStatus string={row?.status} />
          </TableCell>
          <TableCell>
            {row?.v_type?.name || projTextfieldArray[0] || "-"}
          </TableCell>
          <TableCell>
            {row?.brand?.name || projTextfieldArray[1] || "-"}
          </TableCell>
          <TableCell>
            {row?.model?.name || projTextfieldArray[2] || "-"}
          </TableCell>
          <TableCell>
            {<FormatDateTime value={row?.created_at} /> || "-"}
          </TableCell>
          <TableCell>
            {<FormatDateTime value={row?.modified_at} /> || "-"}
          </TableCell>

          <TableCell>
            <Link to={`${userDashboardRoute.projects}/${row?.id}`}>
              <DashboardButton type="filled">
                <Typography variant="p" component="p">
                  <FormattedMessage id="button.details" />
                </Typography>
              </DashboardButton>
            </Link>
          </TableCell>
        </tr>
      );
    });
  };

  // EFFECTS
  useEffect(() => {
    if (!isFilteringActive) {
      resetFiltersAndPage();
      onProjectsPageChange(1);
    }
  }, [isFilteringActive]);

  // UTILS
  const tableHeaders = [
    // "Id",
    <FormattedMessage id="table.header.status" />,
    <FormattedMessage id="table.header.type" />,
    <FormattedMessage id="table.header.brand" />,
    <FormattedMessage id="table.header.model" />,
    <FormattedMessage id="table.header.created" />,
    <FormattedMessage id="table.header.updated" />,
    <FormattedMessage id="table.header.details" />,
  ];

  return (
    <TableWrapper className="projects-table">
      <RowContainer start align="center">
        <RowContainer align="start" minGap className="project-filter">
          <RowContainer align="center" noGap start>
            <FilterButton
              isActive={isFilteringActive}
              disabled={
                projects?.paginated_results?.length === 0 ||
                projects?.paginated_results === null
              }
              onClick={handleToggleFilters}
            >
              <RowContainer center minGap>
                {isFilteringActive ? <FilterAlt /> : <FilterAltOff />}
                <Typography
                  variant="p"
                  color="var(--text-primary) !important"
                  fontWeight="400 !important"
                  fontSize={"16px !important"}
                >
                  <FormattedMessage id="button.filter" />
                </Typography>
              </RowContainer>
            </FilterButton>
          </RowContainer>
        </RowContainer>
        <RowContainer align="center" noGap>
          <div>
            <RowContainer align="center" noGap start>
              <FilterButton
                isActive={isFilteringManualProjectsActive}
                onClick={handleToggleManualFilters}
                disabled={
                  projects?.paginated_results?.length === 0 ||
                  projects?.paginated_results === null
                }
              >
                <RowContainer center minGap>
                  {isFilteringManualProjectsActive ? (
                    <FilterAlt />
                  ) : (
                    <FilterAltOff />
                  )}
                  <Typography
                    variant="p"
                    color="var(--text-primary) !important"
                    fontWeight="400 !important"
                    fontSize={"16px !important"}
                  >
                    <FormattedMessage id="button.filter.manual" />
                  </Typography>
                </RowContainer>
              </FilterButton>
            </RowContainer>
          </div>
        </RowContainer>
      </RowContainer>

      {isFilteringActive ? (
        <MultipleFilterComponent
          filters={filters}
          filterOptions={projects.filter_options}
          handleFilterChange={handleFilterChange}
          tableData={notFilteredProjects || []}
          paginationData={paginationData}
        />
      ) : !!isFilteringManualProjectsActive ? (
        <MultipleFilterComponent
          filters={manualProjectsFilters}
          filterOptions={projects.filter_options_manual}
          handleFilterChange={handleFilterChangeManual}
          tableData={notFilteredProjects || []}
          paginationData={paginationData}
        />
      ) : null}

      {projects?.paginated_results?.length > 0 ? (
        <StyledTable>
          <thead>
            <tr>
              {tableHeaders.map((header, index) => (
                <TableHeader key={index}>{header}</TableHeader>
              ))}
            </tr>
          </thead>
          <tbody>{renderProjectsRows()}</tbody>
        </StyledTable>
      ) : (
        <RowContainer center>
          <Typography
            variant="h6"
            component="h6"
            fontWeight={"400"}
            color="var(--text-secondary)"
          >
            <FormattedMessage id="table.noData" />
          </Typography>
        </RowContainer>
      )}
    </TableWrapper>
  );
});

export default ProjectsList;
